// eslint-disable-next-line @alto/no-pocky-import
import { DeprecatedModal } from '@alto/pocky';
import React, { useCallback, useEffect } from 'react';
import { closeBanner } from '~shared/actions/banners';
import { clearInsuranceError, deselectInsurance } from '~shared/actions/insurances';
import { closeModal } from '~shared/actions/modal';
import { getSelectedInsuranceID } from '~shared/features/insurances/selectors/getInsurances';
import { usePrevious } from '~shared/hooks';
import { useGetInsuranceByID } from '~shared/queries/query-keys/insurances';
import getIsModalShowing from '~shared/selectors/modal/getIsModalShowing';
import getModalCurrentStep from '~shared/selectors/modal/getModalCurrentStep';
import getModalType from '~shared/selectors/modal/getModalType';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { InsuranceFormContainer } from './InsuranceFormContainer';
import { InsurancePhotoForm } from './InsurancePhotoForm';

const UPLOAD_PHOTO_STEP = 1;

const getTitle = (isEditing = false, currentStep = 0) => {
  if (currentStep === UPLOAD_PHOTO_STEP) {
    return 'Add Insurance Card Photos';
  }

  return isEditing ? 'Update Insurance Card' : 'Add Insurance Card';
};

export const InsuranceModal = () => {
  const dispatch = useDispatchShared();
  const selectedInsuranceID = useSelectorShared(getSelectedInsuranceID);
  const { insurance: selectedInsurance } = useGetInsuranceByID(selectedInsuranceID);
  const success = useSelectorShared(
    (state) => state.insurances.createInsuranceSuccess || state.insurances.updateInsuranceSuccess,
  );
  const prevSuccess = usePrevious(success);
  const showAddInsuranceBanner = useSelectorShared(
    (state) => state.banners.showBanner && state.banners.bannerType === 'ADD_INSURANCE',
  );
  const showModal = useSelectorShared(getIsModalShowing) ?? false;
  const modalType = useSelectorShared(getModalType);
  const showInsuranceModal = showModal && modalType === 'INSURANCE_MODAL';
  const currentStep = useSelectorShared(getModalCurrentStep);

  const handleCloseModal = useCallback(() => {
    dispatch(closeModal('INSURANCE_MODAL'));
    dispatch(deselectInsurance());
    dispatch(clearInsuranceError());
  }, [dispatch]);

  useEffect(() => {
    if (!prevSuccess && success) {
      if (showAddInsuranceBanner) {
        dispatch(closeBanner());
      }

      if (showInsuranceModal) {
        handleCloseModal();
      }
    }
  }, [dispatch, handleCloseModal, prevSuccess, showAddInsuranceBanner, showInsuranceModal, success]);

  if (!showInsuranceModal) {
    return null;
  }

  return (
    <DeprecatedModal
      showing={showInsuranceModal}
      onClose={handleCloseModal}
      title={getTitle(!!selectedInsurance, currentStep)}
    >
      {currentStep === UPLOAD_PHOTO_STEP ? (
        <InsurancePhotoForm onCancel={handleCloseModal} />
      ) : (
        <InsuranceFormContainer onCancel={handleCloseModal} />
      )}
    </DeprecatedModal>
  );
};
